import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, Chip, IconButton, LinearProgress, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Pagination, Stack, Switch, FormControlLabel,
} from '@mui/material';
import {
  Search, Refresh, FilterList, Visibility, People, SmartToy, Map as MapIcon,
  Assignment, Notifications, History, Storage, Share, KeyboardArrowDown,
  KeyboardArrowUp, Close, Download, Delete, DeleteSweep,
} from '@mui/icons-material';
import apiClient from '../../utils/apiClient';

// ===== Types =====

interface TableConfig {
  key: string;
  label: string;
  icon: React.ReactElement;
  endpoint: string;
  columns: ColumnDef[];
  filters: FilterDef[];
  deletable?: boolean;  // Allow individual record delete
  bulkDelete?: boolean; // Allow "purge older than N days"
}

interface ColumnDef {
  field: string;
  label: string;
  width?: number;
  render?: (value: any, row: any) => React.ReactNode;
}

interface FilterDef {
  field: string;
  label: string;
  type: 'text' | 'select' | 'boolean' | 'number';
  options?: { value: string; label: string }[];
}

// ===== Renderers =====

const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const colors: Record<string, string> = {
    super_admin: '#b71c1c', admin: '#e65100', customer: '#1565c0',
  };
  return (
    <Chip label={role?.replace('_', ' ')} size="small"
      sx={{ bgcolor: (colors[role] || '#555') + '20', color: colors[role] || '#555',
        fontWeight: 600, textTransform: 'capitalize', fontSize: '0.7rem' }} />
  );
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => {
  const colors: Record<string, string> = {
    info: '#2196f3', warning: '#ff9800', critical: '#f44336', emergency: '#b71c1c',
  };
  return (
    <Chip label={severity} size="small"
      sx={{ bgcolor: (colors[severity] || '#555') + '20', color: colors[severity] || '#555',
        fontWeight: 600, fontSize: '0.7rem' }} />
  );
};

const BoolChip: React.FC<{ value: boolean; trueLabel?: string; falseLabel?: string }> = ({ value, trueLabel = 'Yes', falseLabel = 'No' }) => (
  <Chip label={value ? trueLabel : falseLabel} size="small"
    sx={{ bgcolor: value ? '#4caf5020' : '#f4433620', color: value ? '#4caf50' : '#f44336',
      fontWeight: 600, fontSize: '0.7rem' }} />
);

const DateCell: React.FC<{ value: string | null }> = ({ value }) => {
  if (!value) return <Typography variant="caption" color="text.secondary">—</Typography>;
  const d = new Date(value);
  return (
    <Tooltip title={d.toISOString()}>
      <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
        {d.toLocaleDateString()} {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
    </Tooltip>
  );
};

const JsonCell: React.FC<{ value: any }> = ({ value }) => {
  if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  return (
    <Tooltip title={<pre style={{ margin: 0, fontSize: '0.7rem', maxHeight: 300, overflow: 'auto' }}>{JSON.stringify(value, null, 2)}</pre>}>
      <Typography variant="caption" fontFamily="monospace" sx={{
        maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{str}</Typography>
    </Tooltip>
  );
};

const IdCell: React.FC<{ value: string }> = ({ value }) => (
  <Tooltip title={value}>
    <Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.65rem' }}>
      {value?.slice(0, 8)}…
    </Typography>
  </Tooltip>
);

// ===== Table Configs =====

const TABLE_CONFIGS: TableConfig[] = [
  {
    key: 'users', label: 'Users', icon: <People sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/users',
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => <IdCell value={v} /> },
      { field: 'email', label: 'Email', width: 200 },
      { field: 'role', label: 'Role', width: 110, render: (v) => <RoleBadge role={v} /> },
      { field: 'is_active', label: 'Active', width: 70, render: (v) => <BoolChip value={v} /> },
      { field: 'last_login', label: 'Last Login', width: 140, render: (v) => <DateCell value={v} /> },
      { field: 'last_activity', label: 'Last Activity', width: 140, render: (v) => <DateCell value={v} /> },
      { field: 'created_at', label: 'Created', width: 140, render: (v) => <DateCell value={v} /> },
      { field: 'session_version', label: 'Session', width: 80, render: (v) => <Typography variant="caption" fontFamily="monospace">{v || '—'}</Typography> },
    ],
    filters: [
      { field: 'search', label: 'Search email', type: 'text' },
      { field: 'role', label: 'Role', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'super_admin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin' }, { value: 'customer', label: 'Customer' },
      ]},
      { field: 'is_active', label: 'Active', type: 'boolean' },
    ],
  },
  {
    key: 'robots', label: 'Robots', icon: <SmartToy sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/robots',
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => <IdCell value={v} /> },
      { field: 'serial_number', label: 'Serial', width: 130 },
      { field: 'model_number', label: 'Model', width: 100 },
      { field: 'friendly_name', label: 'Name', width: 100 },
      { field: 'firmware_version', label: 'Firmware', width: 90 },
      { field: 'status', label: 'Status', width: 80, render: (v) => <Chip label={v || 'unknown'} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> },
      { field: 'mode', label: 'Mode', width: 80 },
      { field: 'owner_id', label: 'Owner', width: 90, render: (v) => v ? <IdCell value={v} /> : <Typography variant="caption" color="text.secondary">—</Typography> },
      { field: 'metadata', label: 'Metadata', width: 160, render: (v) => <JsonCell value={v} /> },
      { field: 'robot_config', label: 'Config', width: 140, render: (v) => <JsonCell value={v} /> },
      { field: 'created_at', label: 'Created', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'search', label: 'Search serial/model', type: 'text' },
      { field: 'status', label: 'Status', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'online', label: 'Online' },
        { value: 'offline', label: 'Offline' },
      ]},
      { field: 'mode', label: 'Mode', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'setup', label: 'Setup' },
        { value: 'active', label: 'Active' }, { value: 'restricted', label: 'Restricted' },
      ]},
    ],
  },
  {
    key: 'robot_access', label: 'Access', icon: <Share sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/robot_access', deletable: true,
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'user_id', label: 'User ID', width: 120, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'robot_id', label: 'Robot ID', width: 120, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'permission_level', label: 'Permission', width: 100 },
      { field: 'created_at', label: 'Created', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'search', label: 'Search user/robot ID', type: 'text' },
    ],
  },
  {
    key: 'maps', label: 'Maps', icon: <MapIcon sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/maps',
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'name', label: 'Name', width: 160 },
      { field: 'robot_id', label: 'Robot ID', width: 100, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'user_id', label: 'User ID', width: 100, render: (v) => v ? <IdCell value={String(v)} /> : '—' },
      { field: 'resolution', label: 'Resolution', width: 90, render: (v) => v ? `${v} m/px` : '—' },
      { field: 'map_file_url', label: 'File', width: 140, render: (v) => v ? <Typography variant="caption" fontFamily="monospace" sx={{ maxWidth: 140, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</Typography> : '—' },
      { field: 'created_at', label: 'Created', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'search', label: 'Search map name', type: 'text' },
    ],
  },
  {
    key: 'audit_logs', label: 'Audit Logs', icon: <Assignment sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/audit_logs', deletable: true, bulkDelete: true,
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => <IdCell value={v} /> },
      { field: 'user_id', label: 'User', width: 100, render: (v) => v ? <IdCell value={v} /> : '—' },
      { field: 'action', label: 'Action', width: 140, render: (v) => <Chip label={v} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> },
      { field: 'target_type', label: 'Target Type', width: 90 },
      { field: 'target_id', label: 'Target ID', width: 100, render: (v) => v ? <IdCell value={v} /> : '—' },
      { field: 'details', label: 'Details', width: 250, render: (v) => <JsonCell value={v} /> },
      { field: 'created_at', label: 'Time', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'action', label: 'Action', type: 'select', options: [
        { value: '', label: 'All' },
        { value: 'user_created', label: 'User Created' }, { value: 'user_updated', label: 'User Updated' },
        { value: 'user_deleted', label: 'User Deleted' }, { value: 'robot_created', label: 'Robot Created' },
        { value: 'robot_updated', label: 'Robot Updated' }, { value: 'robot_deleted', label: 'Robot Deleted' },
        { value: 'robot_assigned', label: 'Robot Assigned' },
      ]},
      { field: 'target_type', label: 'Target', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'user', label: 'User' }, { value: 'robot', label: 'Robot' },
      ]},
      { field: 'days', label: 'Days back', type: 'number' },
    ],
  },
  {
    key: 'alert_rules', label: 'Alert Rules', icon: <Notifications sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/alert_rules',
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => <IdCell value={v} /> },
      { field: 'name', label: 'Name', width: 160 },
      { field: 'trigger_type', label: 'Trigger', width: 90, render: (v) => <Chip label={v} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /> },
      { field: 'event_type', label: 'Event', width: 120, render: (v) => <Typography variant="caption" fontFamily="monospace">{v}</Typography> },
      { field: 'robot_serial', label: 'Robot', width: 100 },
      { field: 'severity', label: 'Severity', width: 90, render: (v) => <SeverityBadge severity={v} /> },
      { field: 'cooldown_minutes', label: 'Cooldown', width: 80, render: (v) => `${v}m` },
      { field: 'is_active', label: 'Active', width: 70, render: (v) => <BoolChip value={v} /> },
      { field: 'condition', label: 'Condition', width: 160, render: (v) => <JsonCell value={v} /> },
      { field: 'created_at', label: 'Created', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'search', label: 'Search name/event', type: 'text' },
      { field: 'severity', label: 'Severity', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'info', label: 'Info' },
        { value: 'warning', label: 'Warning' }, { value: 'critical', label: 'Critical' },
        { value: 'emergency', label: 'Emergency' },
      ]},
      { field: 'trigger_type', label: 'Trigger', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'event', label: 'Event' },
        { value: 'threshold', label: 'Threshold' }, { value: 'absence', label: 'Absence' },
        { value: 'pattern', label: 'Pattern' },
      ]},
      { field: 'is_active', label: 'Active only', type: 'boolean' },
    ],
  },
  {
    key: 'alert_history', label: 'Alert History', icon: <History sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/alert_history', deletable: true, bulkDelete: true,
    columns: [
      { field: 'id', label: 'ID', width: 90, render: (v) => <IdCell value={v} /> },
      { field: 'rule_name', label: 'Rule', width: 160 },
      { field: 'robot_serial', label: 'Robot', width: 110 },
      { field: 'event_type', label: 'Event', width: 110, render: (v) => <Typography variant="caption" fontFamily="monospace">{v}</Typography> },
      { field: 'severity', label: 'Severity', width: 90, render: (v) => <SeverityBadge severity={v} /> },
      { field: 'details', label: 'Details', width: 200, render: (v) => <Typography variant="caption" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</Typography> },
      { field: 'email_sent', label: 'Email', width: 70, render: (v) => <BoolChip value={v} trueLabel="Sent" falseLabel="No" /> },
      { field: 'email_error', label: 'Error', width: 120, render: (v) => v ? <Typography variant="caption" color="error" sx={{ maxWidth: 120, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</Typography> : '—' },
      { field: 'triggered_at', label: 'Triggered', width: 140, render: (v) => <DateCell value={v} /> },
    ],
    filters: [
      { field: 'severity', label: 'Severity', type: 'select', options: [
        { value: '', label: 'All' }, { value: 'info', label: 'Info' },
        { value: 'warning', label: 'Warning' }, { value: 'critical', label: 'Critical' },
        { value: 'emergency', label: 'Emergency' },
      ]},
      { field: 'robot_serial', label: 'Robot serial', type: 'text' },
      { field: 'email_sent', label: 'Email sent', type: 'boolean' },
      { field: 'days', label: 'Days back', type: 'number' },
    ],
  },
  {
    key: 'robot_sessions', label: 'Robot Sessions',
    icon: <Storage sx={{ fontSize: 18 }} />,
    endpoint: '/admin/explorer/robot_sessions',
    columns: [
      { field: 'robot_serial', label: 'Robot', width: 150 },
      { field: 'ip_address', label: 'IP Address', width: 140 },
      { field: 'connected_at', label: 'Connected', width: 170, render: (v) => <DateCell value={v} /> },
      { field: 'disconnected_at', label: 'Disconnected', width: 170, render: (v) => v ? <DateCell value={v} /> : <Chip size="small" color="success" label="online" /> },
      { field: 'duration_min', label: 'Duration (min)', width: 120 },
    ],
    filters: [
      { field: 'robot_serial', label: 'Robot serial', type: 'text' },
      { field: 'days', label: 'Last N days', type: 'select', options: [
        { value: '1', label: 'Today' }, { value: '7', label: 'Last 7 days' },
        { value: '30', label: 'Last 30 days' }, { value: '90', label: 'Last 90 days' },
      ] },
      { field: 'date_from', label: 'From (YYYY-MM-DD)', type: 'text' },
      { field: 'date_to', label: 'To (YYYY-MM-DD)', type: 'text' },
    ],
  },
];


// ===== Main Component =====

const DatabaseExplorer: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [tableSummary, setTableSummary] = useState<any>(null);
  const [data, setData] = useState<any>({ total: 0, rows: [] });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(50);
  const [detailRow, setDetailRow] = useState<any>(null);

  const currentConfig = TABLE_CONFIGS[activeTab];

  // Load table overview
  useEffect(() => {
    apiClient.get('/admin/explorer/tables').then(r => setTableSummary(r.data)).catch(() => {});
  }, []);

  // Load data when tab or filters change
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { skip: (page - 1) * rowsPerPage, limit: rowsPerPage };
      // Add active filters
      for (const [key, value] of Object.entries(filters)) {
        if (value !== '' && value !== undefined && value !== null) {
          params[key] = value;
        }
      }
      const res = await apiClient.get(currentConfig.endpoint, { params });
      setData(res.data);
    } catch (e) {
      console.error('Explorer load error:', e);
      setData({ total: 0, rows: [] });
    }
    setLoading(false);
  }, [activeTab, page, filters, currentConfig, rowsPerPage]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTabChange = (_: any, newTab: number) => {
    setActiveTab(newTab);
    setFilters({});
    setPage(1);
    setDetailRow(null);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const clearFilters = () => { setFilters({}); setPage(1); };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / rowsPerPage));
  const [purgeDialog, setPurgeDialog] = useState(false);
  const [purgeDays, setPurgeDays] = useState(30);

  // Delete single record
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await apiClient.delete(`${currentConfig.endpoint}/${id}`);
      loadData();
      apiClient.get('/admin/explorer/tables').then(r => setTableSummary(r.data)).catch(() => {});
    } catch (e: any) {
      alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // Bulk purge older than N days
  const handleBulkPurge = async () => {
    try {
      const res = await apiClient.delete(currentConfig.endpoint, { params: { days: purgeDays } });
      setPurgeDialog(false);
      loadData();
      apiClient.get('/admin/explorer/tables').then(r => setTableSummary(r.data)).catch(() => {});
      alert(`Deleted ${res.data.deleted_count} records older than ${purgeDays} days`);
    } catch (e: any) {
      alert(`Purge failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // CSV export
  const exportCSV = () => {
    if (!data.rows?.length) return;
    const cols = currentConfig.columns.map(c => c.field);
    const header = currentConfig.columns.map(c => c.label).join(',');
    const rows = data.rows.map((row: any) =>
      cols.map(c => {
        const v = row[c];
        if (v === null || v === undefined) return '';
        if (typeof v === 'object') return `"${JSON.stringify(v).replace(/"/g, '""')}"`;
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${currentConfig.key}_export.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Database Explorer</Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only access to all PostgreSQL tables
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {currentConfig.bulkDelete && (
            <Button size="small" startIcon={<DeleteSweep />} color="error" onClick={() => setPurgeDialog(true)}
              disabled={!data.rows?.length}>
              Purge Old
            </Button>
          )}
          <Button size="small" startIcon={<Download />} onClick={exportCSV} disabled={!data.rows?.length}>
            Export CSV
          </Button>
          <Button size="small" startIcon={<Refresh />} onClick={loadData} variant="outlined">
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Table summary cards */}
      {tableSummary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 1, mb: 2 }}>
          {TABLE_CONFIGS.map((t, idx) => (
            <Paper key={t.key} onClick={() => handleTabChange(null, idx)}
              sx={{
                p: 1.5, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                border: activeTab === idx ? '2px solid' : '2px solid transparent',
                borderColor: activeTab === idx ? 'primary.main' : 'transparent',
                '&:hover': { borderColor: 'primary.main', opacity: 0.9 },
              }}>
              <Typography variant="h5" fontWeight={700} color="primary">
                {tableSummary[t.key]?.count?.toLocaleString() || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">{t.label}</Typography>
            </Paper>
          ))}
        </Box>
      )}

      <Card>
        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          {TABLE_CONFIGS.map(t => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={t.label}
              sx={{ minHeight: 48, fontSize: '0.8rem' }} />
          ))}
        </Tabs>

        <CardContent sx={{ p: 2 }}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
            {currentConfig.filters.map(f => {
              if (f.type === 'text') {
                return (
                  <TextField key={f.field} size="small" placeholder={f.label}
                    value={filters[f.field] || ''} sx={{ width: 180 }}
                    onChange={e => handleFilterChange(f.field, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16 }} /></InputAdornment>,
                    }} />
                );
              }
              if (f.type === 'select') {
                return (
                  <FormControl key={f.field} size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>{f.label}</InputLabel>
                    <Select value={filters[f.field] || ''} label={f.label}
                      onChange={e => handleFilterChange(f.field, e.target.value)}>
                      {f.options?.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                );
              }
              if (f.type === 'boolean') {
                return (
                  <FormControlLabel key={f.field} label={f.label}
                    control={
                      <Switch size="small" checked={filters[f.field] === true}
                        onChange={e => handleFilterChange(f.field, e.target.checked || undefined)} />
                    }
                    sx={{ ml: 0 }} />
                );
              }
              if (f.type === 'number') {
                return (
                  <TextField key={f.field} size="small" type="number" label={f.label}
                    value={filters[f.field] || ''} sx={{ width: 100 }}
                    onChange={e => handleFilterChange(f.field, e.target.value ? parseInt(e.target.value) : '')} />
                );
              }
              return null;
            })}
            {Object.keys(filters).length > 0 && (
              <Button size="small" onClick={clearFilters} sx={{ fontSize: '0.75rem' }}>Clear</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {data.total?.toLocaleString() || 0} records
            </Typography>
          </Box>

          {loading && <LinearProgress sx={{ mb: 1 }} />}

          {/* Data Table */}
          {data?.summary && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Chip size="small" variant="outlined" label={`Sessions: ${data.summary.sessions}`} />
          <Chip size="small" variant="outlined" label={`Total online: ${data.summary.total_online_hours} h`} />
          <Chip size="small" variant="outlined" label={`Robots: ${data.summary.robots}`} />
          <Chip size="small" color="success" variant="outlined" label={`Online now: ${data.summary.currently_online}`} />
          {data.summary.ips?.length > 0 && (
            <Tooltip title={data.summary.ips.join(', ')}>
              <Chip size="small" variant="outlined" label={`IPs: ${data.summary.ips.length}`} />
            </Tooltip>
          )}
        </Box>
      )}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 440px)', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, p: 0.5 }} />
                  {currentConfig.columns.map(col => (
                    <TableCell key={col.field} sx={{
                      fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap',
                      width: col.width, minWidth: col.width,
                    }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows?.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={currentConfig.columns.length + 1} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No records found</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {data.rows?.map((row: any, idx: number) => (
                  <TableRow key={row.id || idx} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setDetailRow(detailRow?.id === row.id ? null : row)}>
                    <TableCell sx={{ p: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          {detailRow?.id === row.id
                            ? <KeyboardArrowUp sx={{ fontSize: 16 }} />
                            : <KeyboardArrowDown sx={{ fontSize: 16 }} />
                          }
                        </IconButton>
                        {currentConfig.deletable && row.id && (
                          <Tooltip title="Delete record">
                            <IconButton size="small" sx={{ p: 0.25 }} color="error"
                              onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}>
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {currentConfig.columns.map(col => (
                      <TableCell key={col.field} sx={{ py: 0.75, fontSize: '0.8rem' }}>
                        {col.render ? col.render(row[col.field], row) : (row[col.field] ?? '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)}
                size="small" shape="rounded" />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel Dialog */}
      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Record Detail — {currentConfig.label}
          </Typography>
          <IconButton onClick={() => setDetailRow(null)} size="small"><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {Object.entries(detailRow).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight={600} sx={{ width: 180, flexShrink: 0, color: 'text.secondary' }}>
                    {key}
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', flex: 1 }}>
                    {value === null || value === undefined ? (
                      <Typography component="span" color="text.disabled">null</Typography>
                    ) : typeof value === 'object' ? (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    ) : typeof value === 'boolean' ? (
                      <BoolChip value={value} />
                    ) : (
                      String(value)
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Purge Dialog */}
      <Dialog open={purgeDialog} onClose={() => setPurgeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Purge Old {currentConfig.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Delete all {currentConfig.label.toLowerCase()} records older than:
          </Typography>
          <TextField fullWidth type="number" label="Days" value={purgeDays}
            onChange={(e) => setPurgeDays(Math.max(1, parseInt(e.target.value) || 1))}
            InputProps={{ inputProps: { min: 1, max: 365 } }} />
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeDialog(false)}>Cancel</Button>
          <Button onClick={handleBulkPurge} color="error" variant="contained">
            Delete Records Older Than {purgeDays} Days
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseExplorer;
